import { z } from 'zod';

// src/enums.ts
var PropertyCategorySchema = z.string().trim().min(1).max(60);
var PropertyTypeSchema = z.string().trim().min(1).max(60);
var LISTING_STATUSES = [
  "upcoming",
  "under-construction",
  "possession",
  "ready-to-move",
  "completed",
  "under-maintenance"
];
var ListingStatusSchema = z.enum(LISTING_STATUSES);
var LISTING_STATUS_LABELS = {
  upcoming: "Upcoming",
  "under-construction": "Under Construction",
  possession: "Possession",
  "ready-to-move": "Ready to Move",
  completed: "Completed",
  "under-maintenance": "Under Maintenance"
};
var TRANSACTION_TYPES = ["buy", "rent"];
var TransactionTypeSchema = z.enum(TRANSACTION_TYPES);
var FURNISHING_OPTIONS = ["unfurnished", "semi-furnished", "furnished"];
var FurnishingSchema = z.enum(FURNISHING_OPTIONS);
var PUBLISH_STATUSES = ["draft", "pending", "published", "archived"];
var PublishStatusSchema = z.enum(PUBLISH_STATUSES);
var MEDIA_TYPES = ["image", "video", "floor-plan", "tour-360", "brochure"];
var MediaTypeSchema = z.enum(MEDIA_TYPES);
var BHK_CONFIGS = ["1", "1.5", "2", "2.5", "3", "3.5", "4", "4.5", "5", "5+"];
var BhkConfigSchema = z.enum(BHK_CONFIGS);
var EntityIdSchema = z.string().trim().min(1, "Invalid id").max(160, "Invalid id").describe("Opaque entity id");
var SlugSchema = z.string().min(1).max(160).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Invalid slug");
var GeoPointSchema = z.object({
  type: z.literal("Point"),
  coordinates: z.tuple([
    z.number().min(-180).max(180),
    // longitude
    z.number().min(-90).max(90)
    // latitude
  ]).describe("[lng, lat]")
});
var SeoMetaSchema = z.object({
  title: z.string().max(70).optional(),
  description: z.string().max(180).optional(),
  canonicalUrl: z.string().url().optional(),
  ogImage: z.string().url().optional(),
  noindex: z.boolean().default(false),
  keywords: z.array(z.string()).max(20).default([])
});
var PaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(60).default(12)
});
var PaginationMetaSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
  hasNext: z.boolean(),
  hasPrev: z.boolean()
});
function buildPaginationMeta(total, page, limit) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  return {
    page,
    limit,
    total,
    totalPages,
    hasNext: page < totalPages,
    hasPrev: page > 1
  };
}
var MediaAssetSchema = z.object({
  type: MediaTypeSchema,
  url: z.string().url(),
  /** Cloudinary public id, used for transformations & deletion. */
  publicId: z.string().optional(),
  alt: z.string().max(160).default(""),
  /** BlurHash / LQIP placeholder for progressive loading. */
  blurDataUrl: z.string().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  /** Sort order within its group. */
  order: z.number().int().min(0).default(0)
});
var DeveloperRefSchema = z.object({
  id: EntityIdSchema,
  name: z.string(),
  slug: SlugSchema,
  logo: MediaAssetSchema.optional()
});
var AreaSchema = z.object({
  carpet: z.number().positive().optional(),
  builtUp: z.number().positive().optional(),
  superBuiltUp: z.number().positive().optional()
});
var LocationSchema = z.object({
  city: z.string(),
  locality: z.string(),
  /** Street line, composed from the upstream address parts. Absent when the
   *  source has nothing usable there (some records hold "NA"). */
  address: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  pincode: z.string().regex(/^\d{6}$/, "Invalid pincode").optional(),
  point: GeoPointSchema.optional(),
  /** Neighbouring localities tagged on the listing, besides `locality`.
   *  Optional rather than defaulted so a Location can still be constructed
   *  without it (cards never carry them). */
  nearbyAreas: z.array(z.string()).optional(),
  /** Upstream-provided maps link for the exact pin. */
  googleMapsUrl: z.string().url().optional()
});
var InvestmentSchema = z.object({
  monthlyRent: z.number().min(0).optional(),
  tenant: z.string().optional(),
  leaseTermMonths: z.number().int().min(0).optional(),
  escalationPct: z.number().min(0).max(100).optional(),
  annualCost: z.number().min(0).optional(),
  grossYieldPct: z.number().min(0).optional(),
  netYieldPct: z.number().min(0).optional(),
  capRatePct: z.number().min(0).optional()
});
var ReraSchema = z.object({
  number: z.string().optional(),
  authorityUrl: z.string().url().optional(),
  verified: z.boolean().default(false)
});
var UnitTypeSchema = z.object({
  config: BhkConfigSchema.optional(),
  type: PropertyTypeSchema,
  price: z.number().min(0),
  area: AreaSchema,
  /**
   * The unit `area` is recorded in, verbatim from the source ("sqft", "Square
   * Yard", "Acre", "Bigha", …). Units differ BETWEEN unit types of the same
   * project, so an area is only meaningful shown next to its own label — and
   * two areas may only be compared or ranged when their labels match.
   */
  areaUnit: z.string().optional(),
  totalUnits: z.number().int().min(0).optional()
});
var InventorySchema = z.object({
  totalBlocks: z.number().int().min(0).optional(),
  totalFlats: z.number().int().min(0).optional(),
  totalShops: z.number().int().min(0).optional()
});
var PropertySchema = z.object({
  id: EntityIdSchema,
  slug: SlugSchema,
  title: z.string().min(4).max(160),
  description: z.string().max(8e3).default(""),
  category: PropertyCategorySchema,
  type: PropertyTypeSchema,
  transaction: TransactionTypeSchema.default("buy"),
  config: BhkConfigSchema.optional(),
  // residential only
  listingStatus: ListingStatusSchema,
  furnishing: FurnishingSchema.optional(),
  price: z.number().min(0),
  pricePerSqft: z.number().min(0).optional(),
  area: AreaSchema,
  /** All of this project's real unit configurations — see {@link UnitTypeSchema}. */
  unitTypes: z.array(UnitTypeSchema).default([]),
  location: LocationSchema,
  amenities: z.array(z.string()).default([]),
  media: z.array(MediaAssetSchema).default([]),
  documents: z.array(MediaAssetSchema).default([]),
  developer: DeveloperRefSchema.optional(),
  rera: ReraSchema.optional(),
  verified: z.boolean().default(false),
  isPreLeased: z.boolean().default(false),
  isGiftCity: z.boolean().default(false),
  investment: InvestmentSchema.optional(),
  /** Project-level counts of blocks / flats / shops, where recorded upstream. */
  inventory: InventorySchema.optional(),
  /** When the project was launched, and when it hands over. */
  launchDate: z.string().datetime().optional(),
  possessionDate: z.string().datetime().optional(),
  publishStatus: PublishStatusSchema,
  views: z.number().int().min(0).default(0),
  seo: SeoMetaSchema.optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var PropertyCardSchema = PropertySchema.pick({
  id: true,
  slug: true,
  title: true,
  category: true,
  type: true,
  transaction: true,
  config: true,
  listingStatus: true,
  price: true,
  pricePerSqft: true,
  area: true,
  unitTypes: true,
  location: true,
  verified: true,
  isPreLeased: true,
  isGiftCity: true
}).extend({
  /** Just the primary image for cards. */
  coverImage: MediaAssetSchema.optional(),
  /** First few gallery images, for the card image carousel. */
  images: z.array(MediaAssetSchema).optional(),
  developerName: z.string().optional()
});
var csv = (item) => z.union([z.string(), z.array(z.string())]).optional().transform((v) => {
  if (v == null) return void 0;
  const list = Array.isArray(v) ? v : v.split(",");
  return list.map((s) => s.trim()).filter(Boolean);
}).pipe(z.array(item).optional());
var PROPERTY_SORTS = [
  "relevance",
  "price-asc",
  "price-desc",
  "newest",
  "area-desc"
];
var PropertySortSchema = z.enum(PROPERTY_SORTS).default("relevance");
var PropertySearchQuerySchema = PaginationQuerySchema.extend({
  q: z.string().trim().max(120).optional(),
  transaction: TransactionTypeSchema.optional(),
  category: PropertyCategorySchema.optional(),
  type: csv(PropertyTypeSchema),
  config: csv(BhkConfigSchema),
  listingStatus: csv(ListingStatusSchema),
  city: z.string().optional(),
  locality: z.string().optional(),
  state: z.string().optional(),
  areas: csv(z.string()),
  developer: z.string().optional(),
  priceMin: z.coerce.number().min(0).optional(),
  priceMax: z.coerce.number().min(0).optional(),
  areaMin: z.coerce.number().min(0).optional(),
  areaMax: z.coerce.number().min(0).optional(),
  possessionStart: z.string().trim().optional(),
  possessionEnd: z.string().trim().optional(),
  amenities: csv(z.string()),
  verified: z.coerce.boolean().optional(),
  preLeased: z.coerce.boolean().optional(),
  giftCity: z.coerce.boolean().optional(),
  sort: PropertySortSchema
}).superRefine((val, ctx) => {
  if (val.priceMin != null && val.priceMax != null && val.priceMin > val.priceMax) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["priceMin"],
      message: "priceMin cannot exceed priceMax"
    });
  }
  if (val.areaMin != null && val.areaMax != null && val.areaMin > val.areaMax) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["areaMin"],
      message: "areaMin cannot exceed areaMax"
    });
  }
  if (val.possessionStart && val.possessionEnd && val.possessionStart > val.possessionEnd) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["possessionStart"],
      message: "possessionStart cannot be after possessionEnd"
    });
  }
});
var SearchFacetsSchema = z.object({
  type: z.record(z.string(), z.number()).default({}),
  city: z.record(z.string(), z.number()).default({}),
  listingStatus: z.record(z.string(), z.number()).default({}),
  priceRange: z.object({ min: z.number(), max: z.number() }).optional()
});
var INQUIRY_TYPES = ["enquiry", "visit", "callback", "download"];
var InquiryTypeSchema = z.enum(INQUIRY_TYPES);
var VISIT_MODES = ["in-person", "virtual"];
var VisitModeSchema = z.enum(VISIT_MODES);
var PHONE_SEPARATORS = /[\s\-().]/g;
var INDIAN_E164 = /^\+91[6-9]\d{9}$/;
var INDIAN_BARE = /^[6-9]\d{9}$/;
var INTERNATIONAL = /^\+[1-9]\d{6,14}$/;
var PhoneSchema = z.string().trim().refine((raw) => {
  const digits = raw.replace(PHONE_SEPARATORS, "");
  if (digits.startsWith("+91")) return INDIAN_E164.test(digits);
  if (digits.startsWith("+")) return INTERNATIONAL.test(digits);
  return INDIAN_BARE.test(digits);
}, "Enter a valid mobile number \u2014 include your country code (e.g. +971) if you are outside India");
var VisitSlotSchema = z.object({
  /** ISO date (YYYY-MM-DD). */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date"),
  /** 24h time (HH:mm). */
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Invalid time"),
  mode: VisitModeSchema.default("in-person"),
  /** IANA timezone for NRI-friendly scheduling, e.g. "Asia/Dubai". */
  timezone: z.string().optional()
});
var UtmSchema = z.object({
  source: z.string().max(120).optional(),
  medium: z.string().max(120).optional(),
  campaign: z.string().max(120).optional()
});
var CreateInquirySchema = z.object({
  type: InquiryTypeSchema,
  name: z.string().trim().min(2, "Please enter your name").max(120),
  email: z.string().trim().email("Enter a valid email"),
  phone: PhoneSchema,
  message: z.string().trim().max(1e3).optional(),
  propertySlug: SlugSchema.optional(),
  visitSlot: VisitSlotSchema.optional(),
  consent: z.boolean().optional(),
  utm: UtmSchema.optional(),
  /** Honeypot — must stay empty. */
  website: z.string().max(0).optional()
}).superRefine((val, ctx) => {
  if (val.type === "visit" && !val.visitSlot) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["visitSlot"],
      message: "A visit date and time is required"
    });
  }
});
var InquiryAckSchema = z.object({
  received: z.literal(true)
});
var CONTACT_INTERESTS = [
  "residential",
  "commercial",
  "gift-city",
  "preleased",
  "weekend-homes",
  "land-plot"
];
var ContactInterestSchema = z.enum(CONTACT_INTERESTS);
var CONTACT_INTEREST_LABELS = {
  residential: "Residential",
  commercial: "Commercial",
  "gift-city": "GIFT City",
  preleased: "Pre-Leased",
  "weekend-homes": "Weekend Homes",
  "land-plot": "Land & Plot"
};
var CONTACT_METHODS = ["phone", "email", "whatsapp"];
var ContactMethodSchema = z.enum(CONTACT_METHODS);
var CONTACT_METHOD_LABELS = {
  phone: "Phone",
  email: "Email",
  whatsapp: "WhatsApp"
};
var CONTACT_TIMES = ["anytime", "morning", "afternoon", "evening"];
var ContactTimeSchema = z.enum(CONTACT_TIMES);
var CONTACT_TIME_LABELS = {
  anytime: "Anytime",
  morning: "Morning (9am \u2013 12pm)",
  afternoon: "Afternoon (12pm \u2013 4pm)",
  evening: "Evening (4pm \u2013 8pm)"
};
var SAFE_TEXT_MESSAGE = "This field contains characters that are not allowed";
function isSafeText(value) {
  if (/<\s*\/?\s*[a-z][^>]*>/i.test(value)) return false;
  if (/javascript:|data:text\/html|on\w+\s*=/i.test(value)) return false;
  const links = value.match(/https?:\/\//gi);
  if (links && links.length > 3) return false;
  return true;
}
var ContactMessageInputSchema = z.object({
  fullName: z.string().trim().min(2, "Please enter your full name").max(120).refine(isSafeText, SAFE_TEXT_MESSAGE),
  email: z.string().trim().toLowerCase().email("Enter a valid email address").max(160),
  phone: PhoneSchema,
  company: z.string().trim().max(160).refine(isSafeText, SAFE_TEXT_MESSAGE).optional(),
  city: z.string().trim().max(120).refine(isSafeText, SAFE_TEXT_MESSAGE).optional(),
  interest: z.enum(CONTACT_INTERESTS).optional(),
  subject: z.string().trim().max(160).refine(isSafeText, SAFE_TEXT_MESSAGE).optional(),
  message: z.string().trim().max(2e3, "Message is too long (2000 characters max)").refine(isSafeText, SAFE_TEXT_MESSAGE).optional(),
  // Optional with NO default, deliberately. No form on the site collects
  // these — the contact form asks for name, email, phone, company and message,
  // and nothing else. A default here is not a sensible fallback, it is invented
  // data: it made every notification report "Preferred contact: Phone ·
  // Anytime" as though the sender had chosen it. Absent must stay absent so the
  // email can omit the row entirely.
  preferredContact: ContactMethodSchema.optional(),
  preferredTime: ContactTimeSchema.optional(),
  /** Required — agreement to the Privacy Policy & Terms & Conditions. */
  consent: z.literal(true, {
    errorMap: () => ({ message: "Please agree to the Privacy Policy & Terms & Conditions" })
  }),
  /** Optional opt-in — marketing communication via SMS/RCS/Email/WhatsApp. */
  marketingConsent: z.boolean().default(false),
  utm: UtmSchema.optional(),
  /** Honeypot — must stay empty. */
  website: z.string().max(0).optional()
});
var ContactMessageAckSchema = z.object({
  received: z.literal(true)
});
var JOB_DEPARTMENTS = [
  "sales",
  "marketing",
  "customer-relations",
  "legal-compliance",
  "finance",
  "operations",
  "technology",
  "human-resources"
];
var JobDepartmentSchema = z.enum(JOB_DEPARTMENTS);
var JOB_DEPARTMENT_LABELS = {
  sales: "Sales",
  marketing: "Marketing",
  "customer-relations": "Customer Relations",
  "legal-compliance": "Legal & Compliance",
  finance: "Finance & Accounts",
  operations: "Operations",
  technology: "Technology",
  "human-resources": "Human Resources"
};
var JOB_EMPLOYMENT_TYPES = ["full-time", "part-time", "internship", "contract"];
var JobEmploymentTypeSchema = z.enum(JOB_EMPLOYMENT_TYPES);
var JOB_EMPLOYMENT_TYPE_LABELS = {
  "full-time": "Full-Time",
  "part-time": "Part-Time",
  internship: "Internship",
  contract: "Contract"
};
var SAFE_TEXT_MESSAGE2 = "This field contains characters that are not allowed";
function isSafeText2(value) {
  if (/<\s*\/?\s*[a-z][^>]*>/i.test(value)) return false;
  if (/javascript:|data:text\/html|on\w+\s*=/i.test(value)) return false;
  const links = value.match(/https?:\/\//gi);
  if (links && links.length > 3) return false;
  return true;
}
var JobPostingSchema = z.object({
  id: EntityIdSchema,
  slug: SlugSchema,
  title: z.string(),
  department: JobDepartmentSchema,
  employmentType: JobEmploymentTypeSchema,
  location: z.string(),
  experienceMin: z.number().int().min(0),
  experienceMax: z.number().int().min(0),
  description: z.string(),
  responsibilities: z.array(z.string()),
  requirements: z.array(z.string()),
  createdAt: z.string().datetime()
});
var JobApplicationInputSchema = z.object({
  fullName: z.string().trim().min(2, "Please enter your full name").max(120).refine(isSafeText2, SAFE_TEXT_MESSAGE2),
  email: z.string().trim().toLowerCase().email("Enter a valid email address").max(160),
  phone: PhoneSchema,
  jobId: EntityIdSchema.optional(),
  currentCompany: z.string().trim().max(160).refine(isSafeText2, SAFE_TEXT_MESSAGE2).optional(),
  experienceYears: z.coerce.number({ errorMap: () => ({ message: "Enter your years of experience" }) }).min(0, "Experience cannot be negative").max(50, "Enter a realistic number of years"),
  resumeUrl: z.string().trim().min(1, "Please share a link to your resume").url("Enter a valid link (starting with http:// or https://)").max(500).refine((v) => /^https?:\/\//i.test(v), "Enter a valid URL starting with http(s)://"),
  linkedinUrl: z.string().trim().max(300).refine((v) => v === "" || /^https?:\/\//i.test(v), "Enter a valid URL starting with http(s)://").optional(),
  coverNote: z.string().trim().max(1500, "Note is too long (1500 characters max)").refine(isSafeText2, SAFE_TEXT_MESSAGE2).optional(),
  /** Required — agreement to the Privacy Policy & Terms & Conditions. */
  consent: z.literal(true, {
    errorMap: () => ({ message: "Please agree to the Privacy Policy & Terms & Conditions" })
  }),
  /** Honeypot — must stay empty. */
  website: z.string().max(0).optional()
});
var JobApplicationAckSchema = z.object({
  received: z.literal(true),
  jobTitle: z.string()
});
var FilterOptionItemSchema = z.object({
  value: z.string().trim().min(1).max(60),
  label: z.string().trim().min(1).max(80),
  order: z.number().int().min(0).default(0)
});
var PriceBandSchema = z.object({
  label: z.string().trim().min(1).max(60),
  min: z.number().int().min(0).optional(),
  max: z.number().int().min(0).optional(),
  order: z.number().int().min(0).default(0)
});
var FilterOptionsSchema = z.object({
  categories: z.array(FilterOptionItemSchema),
  cities: z.array(FilterOptionItemSchema),
  states: z.array(FilterOptionItemSchema),
  areas: z.array(FilterOptionItemSchema),
  developers: z.array(FilterOptionItemSchema),
  propertyTypes: z.array(FilterOptionItemSchema),
  bhkConfigs: z.array(FilterOptionItemSchema),
  listingStatuses: z.array(FilterOptionItemSchema),
  amenities: z.array(FilterOptionItemSchema),
  priceBands: z.array(PriceBandSchema),
  /** Years inventory is actually scheduled for possession in, ascending.
   *  Derived from the listings themselves — a year is present only if
   *  something completes in it. `value` is the 4-digit year as a string. */
  possessionYears: z.array(FilterOptionItemSchema),
  updatedAt: z.string().datetime()
});

export { AreaSchema, BHK_CONFIGS, BhkConfigSchema, CONTACT_INTERESTS, CONTACT_INTEREST_LABELS, CONTACT_METHODS, CONTACT_METHOD_LABELS, CONTACT_TIMES, CONTACT_TIME_LABELS, ContactInterestSchema, ContactMessageAckSchema, ContactMessageInputSchema, ContactMethodSchema, ContactTimeSchema, CreateInquirySchema, DeveloperRefSchema, EntityIdSchema, FURNISHING_OPTIONS, FilterOptionItemSchema, FilterOptionsSchema, FurnishingSchema, GeoPointSchema, INQUIRY_TYPES, InquiryAckSchema, InquiryTypeSchema, InventorySchema, InvestmentSchema, JOB_DEPARTMENTS, JOB_DEPARTMENT_LABELS, JOB_EMPLOYMENT_TYPES, JOB_EMPLOYMENT_TYPE_LABELS, JobApplicationAckSchema, JobApplicationInputSchema, JobDepartmentSchema, JobEmploymentTypeSchema, JobPostingSchema, LISTING_STATUSES, LISTING_STATUS_LABELS, ListingStatusSchema, LocationSchema, MEDIA_TYPES, MediaAssetSchema, MediaTypeSchema, PROPERTY_SORTS, PUBLISH_STATUSES, PaginationMetaSchema, PaginationQuerySchema, PhoneSchema, PriceBandSchema, PropertyCardSchema, PropertyCategorySchema, PropertySchema, PropertySearchQuerySchema, PropertySortSchema, PropertyTypeSchema, PublishStatusSchema, ReraSchema, SearchFacetsSchema, SeoMetaSchema, SlugSchema, TRANSACTION_TYPES, TransactionTypeSchema, UnitTypeSchema, UtmSchema, VISIT_MODES, VisitModeSchema, VisitSlotSchema, buildPaginationMeta };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map